import { NgModule } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { PERSISTED_SLICES } from '../persistence';
import { UserStoreEffects } from './effects/user-store.effects';
import { userStoreReducer } from './reducers/user-store.reducer';
import { createPersistedUserStoreSelector } from './selectors/user-store.selector';
import { USER_STORE_FEATURE_KEY, USER_STORE_UNMIRRORED_KEYS } from './user-store.state';
import { PersistedSlice } from '../persistence/persistence.registry';

/**
 * Registers the user-store feature slice — the client-side mirror of the Azure `user-store`
 * backend (see `ENDPOINTS.AzUserStore`). `UserStoreEffects` loads the mirror on `loadUserStore`,
 * writes through to Azure on every value change, and mirrors the slice to the generic persistence
 * layer so mirrored keys stay readable offline. That persisted copy is `localOnly`: it stays on the
 * device and is not part of the device handover — the values themselves already live in Azure.
 * There is no legacy service behind an injection token here — the store is the only
 * implementation.
 *
 * `USER_STORE_UNMIRRORED_KEYS` defaults to `[]` here, so an app that mirrors everything needs no
 * extra wiring. An app that must keep some keys out of the client-side mirror/IndexedDB overrides
 * it downstream, e.g. in its own root module, providing it AFTER importing `UserStoreStoreModule`:
 * ```ts
 * providers: [{ provide: USER_STORE_UNMIRRORED_KEYS, useValue: ['someSensitiveKey'] }]
 * ```
 * The `PERSISTED_SLICES` registration is itself built from that token via a factory provider, so
 * the persisted projection always excludes the same keys the effects do — see
 * `createPersistedUserStoreSelector`.
 */
@NgModule({
    imports: [
        StoreModule.forFeature(USER_STORE_FEATURE_KEY, userStoreReducer),
        EffectsModule.forFeature([UserStoreEffects]),
    ],
    providers: [
        { provide: USER_STORE_UNMIRRORED_KEYS, useValue: [''] },
        {
            provide: PERSISTED_SLICES,
            multi: true,
            useFactory: (unmirroredKeys: string[]): PersistedSlice => ({
                featureKey: USER_STORE_FEATURE_KEY,
                persistSelector: createPersistedUserStoreSelector(unmirroredKeys),
                // The slice is a mirror of the Azure user store; uploading its cache as well would
                // store every value twice, and the cache would come back from Azure as an entry.
                localOnly: true,
            }),
            deps: [USER_STORE_UNMIRRORED_KEYS],
        },
    ],
})
export class UserStoreStoreModule {}
